import React, { useCallback, useRef, useState } from 'react';
import { Image, Tooltip } from 'antd';
import { useObjectURL } from '../hooks/useObjectURL';
import { SeTooltip } from './SeTooltip';
import { usePopupHost, resolvePreviewContainer } from '../PopupHostContext';
import { rteSelectPopupContainer } from '../utils/selectPlacements';
import { resolveUploadFileType } from '../../shared/uploadFileType';

export type UploadChipProps = {
  slotKey: string;
  file: File | string | null;
  text?: string;
  accept?: string;
  tooltip?: string;
  disabled?: boolean;
  multiple?: boolean;
  batch?: boolean;
  exclusionGroup?: string;
  /** 用户在该 slot 选了 1+ 个文件（空态触发）。批量模式下可能 length>1。 */
  onPick: (slotKey: string, files: FileList) => void;
  /** 已填【非图片】chip 重选文件替换该 slot（始终单文件、只替换本 slot）。 */
  onReplace: (slotKey: string, files: FileList) => void;
  onRemove: (slotKey: string) => void;
};

/**
 * 受控的上传 chip 单元。
 *
 * 设计要点：
 * - 空态用 <button> 承载 disabled 语义 + 自带 hidden <input>，避免共享 inputRef
 *   + setAttribute('data-slot') 这种命令式 hack
 * - 已填【非图片】态：可点击重选替换（V1 editorClickHandler.ts:490-581）——
 *   chip 内自带 hidden 单文件 <input>，点 chip 本体触发它，change 走 onReplace 替换本 slot
 * - 已填【图片】态：点击打开大图预览（V1 editorClickHandler.ts:475-487 + index.tsx antd Image），
 *   hover 显示小预览（V1 upload/hooks.ts:39-41）。图片 chip 不带 file input（点击=预览不是替换）
 * - 图片预览的 URL 走 useObjectURL：File 引用不变 → URL 不变 → <img src> 不变
 *   → 浏览器复用解码缓存 → 不闪烁
 */
export const UploadChip: React.FC<UploadChipProps> = React.memo(
  ({
    slotKey,
    file,
    text,
    accept,
    tooltip,
    disabled,
    multiple,
    batch,
    exclusionGroup,
    onPick,
    onReplace,
    onRemove,
  }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const previewHost = usePopupHost();
    const [previewVisible, setPreviewVisible] = useState(false);
    const hasFile = file != null;
    const uploadFileType = resolveUploadFileType(file);
    const showsImageThumbnail = hasFile && uploadFileType === 'image';
    const showsVideoThumbnail = hasFile && uploadFileType === 'video';
    const mediaUrl = useObjectURL(
      showsImageThumbnail || showsVideoThumbnail ? file : null,
    );
    // 气泡 open 受控：锚点是整个 chip 框，但只在 hover info-icon 时开（见 icon 的 mouseEnter/Leave）
    const [tipOpen, setTipOpen] = useState(false);

    const handleClick = useCallback(() => {
      if (disabled) return;
      inputRef.current?.click();
    }, [disabled]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const list = e.target.files;
        if (list && list.length > 0) onPick(slotKey, list);
        e.target.value = '';
      },
      [onPick, slotKey],
    );

    // 已填非图片 chip：点 chip 本体 → 触发隐藏单文件 input → 替换本 slot
    const handleReplaceClick = useCallback(() => {
      if (disabled) return;
      replaceInputRef.current?.click();
    }, [disabled]);

    const handleReplaceChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const list = e.target.files;
        if (list && list.length > 0) onReplace(slotKey, list);
        e.target.value = '';
      },
      [onReplace, slotKey],
    );

    // 已填图片 chip：点 chip 本体 → 打开大图预览
    const handleImageClick = useCallback(() => {
      if (disabled) return;
      setPreviewVisible(true);
    }, [disabled]);

    const handleRemove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onRemove(slotKey);
      },
      [onRemove, slotKey, disabled],
    );

    const fileName = hasFile
      ? typeof file === 'string'
        ? decodeURIComponent(file.split('/').pop()?.split('?')[0] || 'file')
        : file!.name
      : null;
    const labelText = (hasFile ? fileName : text) ?? '';
    let iconEl: React.ReactNode = <i className="iconfont icon-shangchuan1" />;
    if (showsImageThumbnail) {
      iconEl = (
        <img className="upload-icon-img upload-icon-preview" src={mediaUrl} alt="preview" />
      );
    } else if (showsVideoThumbnail) {
      iconEl = (
        <video
          className="upload-icon-img upload-icon-preview"
          src={mediaUrl}
          preload="auto"
          muted
          playsInline
          aria-label={`${labelText} 视频缩略图`}
        />
      );
    } else if (hasFile && uploadFileType === 'audio') {
      iconEl = <i className="iconfont icon-yinpin" />;
    } else if (hasFile) {
      iconEl = <i className="iconfont icon-wenjian" />;
    }

    const labelEl = (
      <span className="upload-label" data-file-name={labelText}>
        {labelText}
      </span>
    );

    const innerEl = (
      <span className={`upload-inner-wrapper${hasFile ? ' has-file' : ''}`}>
        <span className="upload-icon">{iconEl}</span>
        {hasFile && labelText && !showsImageThumbnail ? (
          // 气泡往上推 8px 拉开与 chip 的间距：已填非图片 chip 点本体=重选替换，
          // 气泡若贴着 chip 顶边（实测仅隔 4px），hover 看完文件名鼠标下移极易误点 chip 触发替换。
          <SeTooltip title={labelText} align={{ offset: [0, -8] }} mouseLeaveDelay={0}>
            {labelEl}
          </SeTooltip>
        ) : (
          labelEl
        )}
        {/* info-icon 是唯一的 hover 触发点（mouseEnter/Leave 开关 tipOpen）；
            气泡本体由空态整个 chip 承载（见下方 return），锚点对齐框中心而非 icon。
            onClick stopPropagation+preventDefault：icon 在空态 <button> 内，点它会冒泡触发
            handleClick 打开文件选择器——info-icon 只该 hover 看提示，不该点了就上传。 */}
        {tooltip && (
          <i
            style={{ fontWeight: 100 }}
            className={`iconfont icon-tishi upload-info-icon${hasFile ? ' hidden' : ''}`}
            data-upload-key={slotKey}
            data-tooltip={tooltip}
            onMouseEnter={() => setTipOpen(true)}
            onMouseLeave={() => setTipOpen(false)}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        )}
        <span
          className={`file-remove-btn${hasFile ? '' : ' hidden'}`}
          title="移除文件"
          onClick={hasFile ? handleRemove : undefined}
        >
          ×
        </span>
      </span>
    );

    if (hasFile) {
      // 已填【图片】：点击大图预览 + hover 小预览（不渲染 file input）
      if (showsImageThumbnail) {
        const chipEl = (
          <span
            className={`upload-chip${batch ? ' upload-chip-batch' : ''}`}
            data-type="upload"
            data-key={slotKey}
            data-group={exclusionGroup}
            data-tooltip={tooltip || undefined}
            onClick={handleImageClick}
          >
            {innerEl}
            {/* V1 parity（index.tsx:1651）：隐藏 antd Image，受控 visible，点击 chip 打开大图 */}
            <Image
              style={{ display: 'none' }}
              src={mediaUrl}
              preview={{
                visible: previewVisible,
                onVisibleChange: (v) => setPreviewVisible(v),
                // 全屏大图预览挂到 document.body：脱离 .rich-text-editor 的 backdrop-filter
                // 造成的 containing block（否则 fixed 预览被锁在编辑器框内、遮罩只盖聊天区，
                // 盖不住左侧 layout）。详见 resolvePreviewContainer 注释。
                getContainer: () => resolvePreviewContainer(previewHost),
              }}
            />
          </span>
        );

        return (
          <Tooltip
            // hover 缩略图预览挂 document.body（与 select/cascader 下拉同款 rteSelectPopupContainer）。
            // 为什么不挂 previewHost(.rte-popup-host)：rc-trigger 的 useAlign 用 collectScroller
            // 沿**浮层自己**的 DOM 父链收集 overflow 祖先算可视空间，host 住在 .rte-editor 子树里，
            // 会被消费端（首页聊天页）输入框上方的 overflow:auto 滚动容器裁剪 → 空间算错、placement
            // 判反 → 缩略图飘到错误位置（与下拉总朝上同根因）。挂 body 逃出滚动祖先，placement="top"
            // 的居中/翻转才按真实视口算对。缩略图是 hover 瞬态浮层、antd 受控自管卸载，挂 body 不触发
            // Qiankun removeChild 崩溃（同全屏大图预览的取舍，见 resolvePreviewContainer）。
            getPopupContainer={rteSelectPopupContainer}
            title={
              <div className="se-upload-preview">
                <img className="se-upload-preview-img" src={mediaUrl} alt="preview" />
              </div>
            }
            // 缩略图预览：placement="top" 让 120×120 预览相对 chip **水平居中**（用户反馈
            // topLeft 左对齐没居中）；align.offset 上移一截，让出 chip 顶部 .file-remove-btn
            // （top:-6px）的点击区——否则预览底边贴着 chip 顶边，盖住删除按钮点不到（用户反馈）。
            placement="top"
            align={{ offset: [0, -10] }}
            arrow={false}
            overlayInnerStyle={{
              background: '#fff',
              padding: 0,
              borderRadius: 4,
              boxShadow: '0 2px 12px 0 rgba(153, 153, 153, 0.25)',
            }}
          >
            {chipEl}
          </Tooltip>
        );
      }

      // 已填【非图片】：点击重选替换本 slot（始终单文件）
      return (
        <span
          className={`upload-chip${batch ? ' upload-chip-batch' : ''}`}
          data-type="upload"
          data-key={slotKey}
          data-group={exclusionGroup}
          data-tooltip={tooltip || undefined}
          onClick={handleReplaceClick}
        >
          {innerEl}
          <input
            ref={replaceInputRef}
            type="file"
            accept={accept}
            multiple={false}
            style={{ display: 'none' }}
            onChange={handleReplaceChange}
          />
        </span>
      );
    }

    const emptyBtn = (
      <button
        type="button"
        className={`upload-chip${batch ? ' upload-chip-batch' : ''}`}
        data-type="upload"
        data-key={slotKey}
        data-group={exclusionGroup}
        data-tooltip={tooltip || undefined}
        onClick={handleClick}
        disabled={disabled}
      >
        {innerEl}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />
      </button>
    );

    // 气泡锚点 = 整个 chip 框（对齐框中心），但触发仅限 hover info-icon：
    // SeTooltip 受控 open + trigger=[] 禁用框自身 hover，开关完全由 info-icon 的 mouseEnter/Leave 驱动。
    return tooltip ? (
      <SeTooltip title={tooltip} open={tipOpen} trigger={[]}>
        {emptyBtn}
      </SeTooltip>
    ) : emptyBtn;
  },
);
UploadChip.displayName = 'UploadChip';
